/**
 * Aspect Ratio Navigation Helpers
 * 
 * Utilities for handling navigation with automatic aspect ratio conversion
 * for cloned projects across all landing pages.
 */

import { Project } from '../../services/projectService';

/**
 * Add aspect ratio conversion params to URL if project was cloned
 * with a different aspect ratio
 * 
 * @param queryParams - URLSearchParams object to append params to
 * @param project - Project object to check for cloning metadata
 * @param logPrefix - Optional prefix for console logs (e.g., "[CREATE CONTENT]")
 */
export const addAspectRatioConversionParams = (
  queryParams: URLSearchParams,
  project: Project,
  logPrefix: string = ''
): void => {
  // Check if project was cloned from another project
  if (!project.metadata?.clonedFrom) {
    return;
  }

  const originalRatio = project.metadata.originalAspectRatio; // e.g., '16:9'
  const targetRatio = project.metadata.aspectRatio;           // e.g., '9:16'
  
  // Only add params if ratios are different
  if (originalRatio && targetRatio && originalRatio !== targetRatio) {
    console.log(
      `✅ ${logPrefix} Adding auto-conversion params: ${originalRatio} → ${targetRatio}`
    );
    
    queryParams.append('source_ratio', originalRatio);
    queryParams.append('target_ratio', targetRatio);
    queryParams.append('auto_convert', 'true');
  }
};

/**
 * Build navigation URL for continuing a project with automatic
 * aspect ratio conversion for cloned projects
 * 
 * @param project - Project to continue
 * @param baseParams - Base URLSearchParams (project-id, audio-url, etc.)
 * @param logPrefix - Optional prefix for console logs
 * @returns Complete URL search string
 */
export const buildProjectNavigationURL = (
  project: Project,
  baseParams: URLSearchParams,
  logPrefix: string = ''
): string => {
  addAspectRatioConversionParams(baseParams, project, logPrefix);
  return baseParams.toString();
};
